import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  AlertCircle, 
  CheckCircle2, 
  Clock,
  Euro,
  Percent
} from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-stone-500">Chargement des statistiques...</div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <div className="text-center text-stone-500">Erreur lors du chargement des statistiques</div>
      </div>
    );
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-300';
      default: return 'bg-blue-100 text-blue-700 border-blue-300';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-stone-900">Tableau de Bord</h1>
        <p className="text-stone-600 mt-1">Vue d'ensemble de votre activité</p>
      </div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* CA Mois */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-600">CA du Mois</CardTitle>
            <Euro className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{(stats.ca_mois || 0).toLocaleString('fr-FR')}€</div>
            <p className="text-xs text-stone-500 mt-1">Devis signés ce mois</p>
          </CardContent>
        </Card>

        {/* CA Année */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-600">CA de l'Année</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{(stats.ca_annee || 0).toLocaleString('fr-FR')}€</div>
            <p className="text-xs text-stone-500 mt-1">Total année en cours</p>
          </CardContent>
        </Card>

        {/* Taux Conversion */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-600">Taux de Conversion</CardTitle>
            <Percent className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.taux_conversion || 0}%</div>
            <p className="text-xs text-stone-500 mt-1">{stats.stats?.devis_signes || 0} / {stats.stats?.total_devis || 0} devis signés</p>
          </CardContent>
        </Card>

        {/* Paiements */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-600">Paiements</CardTitle>
            <DollarSign className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{(stats.paiements_en_attente || 0).toLocaleString('fr-FR')}€</div>
            <p className="text-xs text-stone-500 mt-1">En attente</p>
            <p className="text-xs text-green-600 mt-1">{(stats.paiements_recus || 0).toLocaleString('fr-FR')}€ reçus</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Statistiques Rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-stone-50 rounded-lg">
              <p className="text-2xl font-bold text-stone-900">{stats.stats?.total_devis || 0}</p>
              <p className="text-sm text-stone-600">Total Devis</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-700">{stats.stats?.devis_signes || 0}</p>
              <p className="text-sm text-green-600">Devis Signés</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-700">{stats.stats?.devis_en_attente || 0}</p>
              <p className="text-sm text-amber-600">En Attente</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-700">{stats.stats?.total_evenements || 0}</p>
              <p className="text-sm text-blue-600">Événements</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Prochains Événements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-600" />
              Prochains Événements (7 jours)
            </CardTitle>
            <CardDescription>Événements à venir cette semaine</CardDescription>
          </CardHeader>
          <CardContent>
            {!stats.prochains_evenements || stats.prochains_evenements.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-4">Aucun événement prévu</p>
            ) : (
              <div className="space-y-3">
                {stats.prochains_evenements.map((event) => (
                  <Link
                    key={event.id}
                    to={`/backoffice/finances/${event.id}`}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-stone-50 transition"
                  >
                    <div>
                      <p className="font-medium text-sm">{event.prestation || 'Événement'}</p>
                      <p className="text-xs text-stone-500">
                        {new Date(event.date).toLocaleDateString('fr-FR', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long' 
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm text-amber-600">{(event.montant || 0).toLocaleString('fr-FR')}€</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alertes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Alertes & Notifications
            </CardTitle>
            <CardDescription>Actions requises</CardDescription>
          </CardHeader>
          <CardContent>
            {!stats.alertes || stats.alertes.length === 0 ? (
              <div className="text-center py-4">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-green-600 font-medium">Tout est à jour !</p>
                <p className="text-xs text-stone-500">Aucune alerte</p>
              </div>
            ) : (
              <div className="space-y-2">
                {stats.alertes.map((alerte, idx) => (
                  <div
                    key={idx}
                    className={`p-3 border rounded-lg ${getSeverityColor(alerte.severity)}`}
                  >
                    <div className="flex items-start gap-2">
                      {alerte.severity === 'high' ? (
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium">{alerte.message}</p>
                        {alerte.evenement_id && (
                          <Link
                            to={`/backoffice/finances/${alerte.evenement_id}`}
                            className="text-xs underline mt-1 inline-block"
                          >
                            Voir l'événement →
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
